import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckSquare, Plus, Trash, PencilSimple, X as XIcon, Calendar, ClockCounterClockwise, Check, UserCircle } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { timeAgo } from "@/lib/format";

// §7 — initials for the checklist-item assignee chip.
const _initials = (name) => (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

/* Feature E — Key Date widget */
export function KeyDateWidget({ ticket, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(ticket.key_date || "");
  const [label, setLabel] = useState(ticket.key_date_label || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDate(ticket.key_date || ""); setLabel(ticket.key_date_label || ""); }, [ticket.key_date, ticket.key_date_label]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/tickets/${ticket.id}`, {
        key_date: date || null,
        key_date_label: label.trim() || null,
      });
      setEditing(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try { await api.patch(`/tickets/${ticket.id}`, { key_date: null, key_date_label: null }); setEditing(false); onSaved?.(); }
    finally { setSaving(false); }
  };

  const nice = ticket.key_date ? new Date(ticket.key_date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";
  const daysAway = ticket.key_date ? Math.round((new Date(ticket.key_date) - new Date()) / 86400000) : null;
  const relative = daysAway === null ? "" : daysAway === 0 ? "today" : daysAway > 0 ? `in ${daysAway} day${daysAway === 1 ? "" : "s"}` : `${-daysAway} day${daysAway === -1 ? "" : "s"} ago`;

  if (editing) {
    return (
      <div className="hairline rounded-lg p-4 bg-white" data-testid="key-date-editor">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">Key date</div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="text-[11px] text-slate-600">Label</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Start Date" className="mt-0.5 bg-white border-slate-200 h-9" data-testid="key-date-label-input" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-[11px] text-slate-600">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-0.5 bg-white border-slate-200 h-9" data-testid="key-date-date-input" />
          </div>
          <div className="flex gap-1">
            <Button onClick={save} disabled={saving} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9" data-testid="key-date-save">Save</Button>
            <Button onClick={() => setEditing(false)} variant="ghost" className="h-9 text-slate-600">Cancel</Button>
            {ticket.key_date && <Button onClick={clear} variant="ghost" className="h-9 text-red-500 hover:text-red-600">Clear</Button>}
          </div>
        </div>
      </div>
    );
  }

  if (!ticket.key_date) {
    return (
      <button onClick={() => setEditing(true)} className="w-full hairline rounded-lg p-3 bg-slate-50/60 hover:bg-slate-100/60 text-left text-xs text-slate-500 flex items-center gap-2" data-testid="key-date-empty">
        <Calendar size={14} weight="duotone" /> Add a key date (e.g. start date)
      </button>
    );
  }

  return (
    <div className="hairline rounded-lg p-4 bg-gradient-to-br from-[#4f4c90]/8 to-[#4f4c90]/2 border-[#4f4c90]/30 flex items-center gap-4 relative group" data-testid="key-date-widget">
      <div className="w-14 h-14 rounded-lg bg-white border border-[#4f4c90]/40 flex flex-col items-center justify-center flex-shrink-0">
        <div className="text-[9px] uppercase tracking-wider text-[#4f4c90]">{new Date(ticket.key_date).toLocaleDateString("en-GB", { month: "short" })}</div>
        <div className="font-heading text-xl font-bold text-[#4f4c90] leading-none">{new Date(ticket.key_date).getDate()}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#4f4c90] font-medium">{ticket.key_date_label || "Key date"}</div>
        <div className="font-heading text-lg font-semibold text-slate-900 truncate">{nice}</div>
        {relative && <div className="text-xs text-slate-600 font-mono mt-0.5">{relative}</div>}
      </div>
      <button onClick={() => setEditing(true)} className="p-1.5 text-slate-500 hover:text-[#4f4c90] rounded opacity-0 group-hover:opacity-100 transition" data-testid="key-date-edit" title="Edit"><PencilSimple size={14} /></button>
    </div>
  );
}

/* Feature A — Checklist section */
export function ChecklistSection({ ticket, onChanged }) {
  const items = [...(ticket.checklist_items || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState("");
  const [editDraft, setEditDraft] = useState("");
  // §7 — team member list for the assignee picker.
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/users").then((r) => {
      // Only agents + admins get assignment dropdown targets; requesters
      // aren't part of the fulfilment team.
      setUsers((r.data || []).filter((u) => u.role !== "requester"));
    }).catch(() => setUsers([]));
  }, []);

  const done = items.filter((i) => i.is_checked).length;
  const total = items.length;

  const add = async () => {
    if (!newLabel.trim()) return;
    await api.post(`/tickets/${ticket.id}/checklist`, { label: newLabel.trim() });
    setNewLabel("");
    onChanged?.();
  };
  const toggle = async (it) => { await api.patch(`/tickets/${ticket.id}/checklist/${it.id}`, { is_checked: !it.is_checked }); onChanged?.(); };
  const remove = async (it) => { await api.delete(`/tickets/${ticket.id}/checklist/${it.id}`); onChanged?.(); };
  const commitEdit = async (it) => {
    const n = editDraft.trim();
    if (n && n !== it.label) await api.patch(`/tickets/${ticket.id}/checklist/${it.id}`, { label: n });
    setEditingId(""); onChanged?.();
  };
  const move = async (it, dir) => {
    const arr = [...items];
    const idx = arr.findIndex((x) => x.id === it.id);
    const other = idx + dir;
    if (other < 0 || other >= arr.length) return;
    [arr[idx], arr[other]] = [arr[other], arr[idx]];
    await api.post(`/tickets/${ticket.id}/checklist/reorder`, { ordered_ids: arr.map((x) => x.id) });
    onChanged?.();
  };
  // §7 — assign / unassign a checklist item.
  const assign = async (it, assigneeId) => {
    try {
      await api.post(`/tickets/${ticket.id}/checklist/${it.id}/assign`, {
        assignee_id: assigneeId || null,
      });
      onChanged?.();
      const label = assigneeId
        ? `Assigned to ${users.find((u) => u.id === assigneeId)?.name || "user"}`
        : "Unassigned";
      toast.success(label);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    }
  };

  return (
    <div className="hairline rounded-lg bg-white p-4" data-testid="ticket-checklist">
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading font-semibold flex items-center gap-2">
          <CheckSquare size={16} weight="duotone" className="text-[#4f4c90]" /> Checklist
        </div>
        {total > 0 && (
          <div className="text-xs text-slate-500 font-mono">{done}/{total} complete</div>
        )}
      </div>
      {total > 0 && (
        <div className="h-1 bg-slate-100 rounded overflow-hidden mb-3">
          <div className="h-full bg-[#39A36E] transition-all" style={{ width: `${(done / total) * 100}%` }} />
        </div>
      )}
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={it.id} className="flex items-center gap-2 py-1 group hover:bg-slate-50/50 rounded px-1" data-testid={`checklist-item-${it.id}`}>
            <Checkbox checked={it.is_checked} onCheckedChange={() => toggle(it)} data-testid={`checklist-toggle-${it.id}`} />
            {editingId === it.id ? (
              <>
                <Input autoFocus value={editDraft} onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") commitEdit(it); if (e.key === "Escape") setEditingId(""); }}
                  className="h-7 bg-white border-slate-200 text-sm flex-1" data-testid={`checklist-edit-input-${it.id}`} />
                <button onClick={() => commitEdit(it)} className="p-1 text-emerald-600"><Check size={12} /></button>
                <button onClick={() => setEditingId("")} className="p-1 text-slate-500"><XIcon size={12} /></button>
              </>
            ) : (
              <>
                <span className={`flex-1 text-sm ${it.is_checked ? "line-through text-slate-400" : "text-slate-800"}`}>{it.label}</span>
                {/* §7 — Assignee chip / picker */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid={`checklist-assign-${it.id}`}
                      title={it.assignee_name ? `Assigned to ${it.assignee_name}` : "Assign to team member"}
                      className={`shrink-0 inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full text-[10px] font-medium transition ${
                        it.assignee_id
                          ? "bg-[#4f4c90]/15 text-[#4f4c90] hover:bg-[#4f4c90]/25"
                          : "opacity-40 group-hover:opacity-100 text-slate-500 hover:text-[#4f4c90] hover:bg-slate-100"
                      }`}
                    >
                      {it.assignee_id
                        ? _initials(it.assignee_name)
                        : <UserCircle size={14} weight="duotone" />}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white max-h-72 overflow-y-auto">
                    <DropdownMenuLabel>Assign to</DropdownMenuLabel>
                    {it.assignee_id && (
                      <>
                        <DropdownMenuItem
                          data-testid={`checklist-unassign-${it.id}`}
                          onClick={() => assign(it, null)}
                          className="text-slate-500"
                        >
                          — unassign —
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {users.length === 0 && (
                      <DropdownMenuItem disabled>No team members</DropdownMenuItem>
                    )}
                    {users.map((u) => (
                      <DropdownMenuItem
                        key={u.id}
                        data-testid={`checklist-assign-${it.id}-user-${u.id}`}
                        onClick={() => assign(it, u.id)}
                      >
                        <span className="inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-slate-100 text-[10px] font-medium text-slate-700 mr-2">
                          {_initials(u.name)}
                        </span>
                        {u.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition">
                  <button onClick={() => move(it, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-700 text-[10px] disabled:opacity-30 px-1">↑</button>
                  <button onClick={() => move(it, 1)} disabled={i === items.length - 1} className="text-slate-400 hover:text-slate-700 text-[10px] disabled:opacity-30 px-1">↓</button>
                  <button onClick={() => { setEditingId(it.id); setEditDraft(it.label); }} className="p-1 text-slate-500 hover:text-[#4f4c90]" data-testid={`checklist-edit-${it.id}`}><PencilSimple size={12} /></button>
                  <button onClick={() => remove(it)} className="p-1 text-slate-500 hover:text-red-600" data-testid={`checklist-delete-${it.id}`}><Trash size={12} /></button>
                </div>
              </>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-slate-500 italic py-2">No checklist items yet. Add one below.</div>}
      </div>
      <div className="flex gap-1 mt-3">
        <Input placeholder="Add an item…" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          className="bg-white border-slate-200 h-8 text-sm" data-testid="checklist-new-input" />
        <Button onClick={add} disabled={!newLabel.trim()} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-8 text-xs gap-1" data-testid="checklist-add-btn"><Plus size={12} /> Add</Button>
      </div>
    </div>
  );
}

/* Feature B — inline edit for subject + description, and history section */
export function InlineEditText({ value, onSave, singleLine = true, className = "", testId, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  if (editing) {
    return (
      <div className="w-full">
        {singleLine ? (
          <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { onSave(draft); setEditing(false); } if (e.key === "Escape") setEditing(false); }}
            className={`bg-white border-slate-200 ${className}`} data-testid={testId} />
        ) : (
          <Textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            className={`bg-white border-slate-200 min-h-24 ${className}`} data-testid={testId} />
        )}
        <div className="flex gap-1 mt-1">
          <Button onClick={() => { onSave(draft); setEditing(false); }} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-7 text-xs">Save</Button>
          <Button onClick={() => setEditing(false)} variant="ghost" className="h-7 text-xs text-slate-600">Cancel</Button>
        </div>
      </div>
    );
  }
  return (
    <button onClick={() => setEditing(true)} className={`text-left group inline-flex items-center gap-1.5 hover:bg-slate-50/60 rounded px-1 -mx-1 ${className}`} data-testid={testId ? `${testId}-trigger` : undefined}>
      <span>{value || <span className="text-slate-400 italic">{placeholder}</span>}</span>
      <PencilSimple size={12} className="text-slate-400 opacity-0 group-hover:opacity-100 transition flex-shrink-0" />
    </button>
  );
}

export function HistorySection({ ticket, users }) {
  const history = [...(ticket.field_history || [])].sort((a, b) => (b.changed_at || "").localeCompare(a.changed_at || ""));
  if (history.length === 0) return null;
  const userName = (id) => users.find((u) => u.id === id)?.name || "someone";
  return (
    <div className="hairline rounded-lg bg-white p-4" data-testid="ticket-history">
      <div className="font-heading font-semibold mb-3 flex items-center gap-2">
        <ClockCounterClockwise size={16} weight="duotone" className="text-slate-500" /> Edit history
      </div>
      <div className="space-y-3">
        {history.map((h) => (
          <div key={h.id} className="text-xs text-slate-600" data-testid={`history-${h.id}`}>
            <div className="text-[11px]">
              <b>{userName(h.changed_by)}</b> edited <b className="capitalize">{h.field_name}</b> · <span className="font-mono text-slate-500">{timeAgo(h.changed_at)}</span>
            </div>
            <div className="mt-1 hairline rounded p-2 bg-red-50/40 text-red-900 text-[11px] line-through decoration-red-400 whitespace-pre-wrap break-words">{h.old_value || <em>(empty)</em>}</div>
            <div className="mt-1 hairline rounded p-2 bg-emerald-50/40 text-emerald-900 text-[11px] whitespace-pre-wrap break-words">{h.new_value || <em>(empty)</em>}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
